import {APIService} from "./api.service";
import {map, Observable, of, tap} from "rxjs";
import {Injectable} from "@angular/core";
import {IAdminAuditEvent} from "../interfaces/audit/IAdminAuditEvent";
import {createStore, select, setProp, withProps} from "@ngneat/elf";
import {getAllEntities, setEntities, withEntities} from "@ngneat/elf-entities";
import {IPlayerAuditEvent} from "../interfaces/audit/IPlayerAuditEvent";
import {IAuditEvent} from "../interfaces/audit/IAuditEvent";
import {IAuditEventType} from "../interfaces/audit/IAuditEventTypes";
import {IAdminAuditFilter} from "../interfaces/audit/IAdminAuditFilter";
import {HttpParams} from "@angular/common/http";
import {IPlayerAuditFilter} from "../interfaces/audit/IPlayerAuditFilter";
import {IPlayerAuditPagedResponse} from "../interfaces/audit/IPlayerAuditPagedResponse";
import {IPaginationFilter} from "../interfaces/IPaginationFilter";
import {IAdminAuditPagedResponse} from "../interfaces/audit/IAdminAuditPagedResponse";
import {IPagedResponse} from "../interfaces/audit/IPagedResponse";
import {ISystemAuditPagedResponse} from "../interfaces/audit/ISystemAuditPagedResponse";
import {EventSourceType} from "../enum/EventSourceType";
import {IErrorType} from "../interfaces/audit/IErrorType";
import {IFailedAuditFilter} from "../interfaces/audit/IFailedAuditFilter";
import {IFailedAuditPagedResponse} from "../interfaces/audit/IFailedAuditPagedResponse";
import {IFailedAuditEvent} from "../interfaces/audit/IFailedAuditEvent";

@Injectable({
  providedIn: 'root'
})
export class AuditingService {

  private defaultAuditRes: IPagedResponse = {
    Data: [],
    Errors: [],
    FirstPage: 1,
    LastPage: 1,
    Message: '',
    NextPage: '',
    PageNumber: 1,
    PageSize: 1,
    PreviousPage: '',
    Succeeded: false,
    TotalPages: 1,
    TotalRecords: 0
  }
  private playerAuditEventStore = createStore(
    {name: 'player-audit-reponse'},
    withProps<{ playerAuditEventResponse: IPlayerAuditPagedResponse }>({playerAuditEventResponse: this.defaultAuditRes})
  );

  private adminAuditEventStore = createStore(
    {name: 'admin-audit-store'},
    withProps<{ adminAuditEventResponse: IAdminAuditPagedResponse }>({adminAuditEventResponse: this.defaultAuditRes})
  );

  private systemAuditEventStore = createStore(
    {name: 'system-audit-store'},
    withProps<{ systemAuditEventResponse: ISystemAuditPagedResponse }>({systemAuditEventResponse: this.defaultAuditRes})
  );

  private auditEventTypeStore = createStore(
    {name: 'audit-event-type-store'},
    withEntities<IAuditEventType, 'Id'>({idKey: 'Id'})
  );

  private errorTypeStore = createStore(
    {name: 'error-type-store'},
    withEntities<IErrorType, 'Id'>({idKey: 'Id'})
  );

  private failedAuditStore = createStore(
    {name: 'failed-audit-store'},
    withProps<{ failedAuditResponse: IFailedAuditPagedResponse }>({failedAuditResponse: this.defaultAuditRes})
  );

  public playerAudits$ = this.playerAuditEventStore.pipe(select(state => state.playerAuditEventResponse));
  public adminAudits$ = this.adminAuditEventStore.pipe(select(state => state.adminAuditEventResponse));
  public systemAudits$ = this.systemAuditEventStore.pipe(select(state => state.systemAuditEventResponse));
  public failedAudits$ = this.failedAuditStore.pipe(select(state => state.failedAuditResponse));
  public errorTypes$ = this.errorTypeStore.pipe(select((state) => getAllEntities()(state)));

  constructor(private apiService: APIService) {
  }

  public retrievePlayerAudits(filterParams: IPlayerAuditFilter): Observable<IPlayerAuditEvent[]> {
    let queryParams: HttpParams = new HttpParams();

    if (filterParams) {
      if (filterParams.playerId) {
        queryParams = queryParams.append('playerId', filterParams.playerId)
      }

      queryParams = this.addDefaultQueryParams(queryParams, filterParams);
    }
    return this.apiService.MakeGetRequest<IPlayerAuditPagedResponse>(`audit/player`, queryParams).pipe(map((playerAuditsP) => {
      this.playerAuditEventStore.update(setProp('playerAuditEventResponse', playerAuditsP));
      return playerAuditsP.Data;
    }));
  }

  public retrieveMetaDataForAuditEvent(eventSourceP: number, auditId: string, gameId: string = "") {
    let queryParams: HttpParams = new HttpParams();

    let apiEndpoint = '';

    if (eventSourceP == EventSourceType.WebApp) {
      apiEndpoint = `audit/player-meta-data/${auditId}`;
    } else if (eventSourceP == EventSourceType.AdminApp) {
      apiEndpoint = `audit/admin-meta-data/${auditId}`;
    } else if (eventSourceP == EventSourceType.System) {
      apiEndpoint = `audit/system-meta-data/${auditId}`;
    }

    if(gameId) {
      apiEndpoint += `/${gameId}`
    }

    return this.apiService.MakeGetRequest<string>(apiEndpoint, queryParams).pipe(map((val) => {
      return JSON.parse(val);
    }));
  };

  public retrieveAdminAudits(filterParams?: IAdminAuditFilter): Observable<IAdminAuditEvent[]> {
    let queryParams: HttpParams = new HttpParams();

    if (filterParams) {
      if (filterParams.adminId) {
        queryParams = queryParams.append('adminId', filterParams.adminId)
      }

      queryParams = this.addDefaultQueryParams(queryParams, filterParams);
    }

    return this.apiService.MakeGetRequest<IAdminAuditPagedResponse>(`audit/admin`, queryParams).pipe(map((adminAuditsP) => {
      this.adminAuditEventStore.update(setProp('adminAuditEventResponse', adminAuditsP));
      return adminAuditsP.Data;
    }));
  }

  private addDefaultQueryParams(paramsP: HttpParams, filterP: IPaginationFilter): HttpParams {
    if (filterP.eventTypeValue) {
      paramsP = paramsP.append('eventTypeValue', filterP.eventTypeValue)
    }
    if (filterP.pageNumber) {
      paramsP = paramsP.append('pageNumber', filterP.pageNumber)
    }
    if (filterP.pageSize) {
      paramsP = paramsP.append('pageSize', filterP.pageSize)
    }
    if (filterP.fromDate) {
      paramsP = paramsP.append('fromDate', filterP.fromDate.toDateString())
    }
    if (filterP.toDate) {
      paramsP = paramsP.append('toDate', filterP.toDate.toDateString())
    }
    if (filterP.gameId) {
      paramsP = paramsP.append('gameId', filterP.gameId)
    }
    if (filterP.genericContentSearch) {
      paramsP = paramsP.append('genericContentSearch', filterP.genericContentSearch);
    }
    if (filterP.isSuccess != null) {
      paramsP = paramsP.append('isSuccess', filterP.isSuccess);
    }
    return paramsP;
  }

  public retrieveSystemAudits(filterParams: IPaginationFilter): Observable<IAuditEvent[]> {

    let queryParams: HttpParams = new HttpParams();
    queryParams = this.addDefaultQueryParams(queryParams, filterParams);

    return this.apiService.MakeGetRequest<ISystemAuditPagedResponse>(`audit/system`, queryParams).pipe(map((systemAuditsP) => {
      this.systemAuditEventStore.update(setProp('systemAuditEventResponse', systemAuditsP));
      return systemAuditsP.Data;
    }));
  }

  public retrieveAllEventTypes(gameIdP: string = ""): Observable<IAuditEventType[]> {
    let apiUrl = 'audit/audit-event-types';

    if(gameIdP) {
      apiUrl += `/${gameIdP}`
    }

    return this.apiService.MakeGetRequest<IAuditEventType[]>(apiUrl).pipe(tap((auditEventTypesP) => {
      this.auditEventTypeStore.update(setEntities(auditEventTypesP));
    }));
  }

  public getAuditEventTypes(): IAuditEventType[] {
    return this.auditEventTypeStore.query(getAllEntities());
  }

  public retrieveErrorTypes(): Observable<IErrorType[]> {
    const cachedErrorTypes = this.errorTypeStore.query(getAllEntities());
    if (cachedErrorTypes.length > 0) {
      return of(cachedErrorTypes);
    }

    return this.apiService.MakeGetRequest<IErrorType[]>('audit/error-types').pipe(tap((errorTypesP) => {
      this.errorTypeStore.update(setEntities(errorTypesP));
    }));
  }

  public getErrorTypes(): IErrorType[] {
    return this.errorTypeStore.query(getAllEntities());
  }

  public getErrorTypeName(errorTypeValue: number): string {
    const errorTypes = this.getErrorTypes();
    const errorType = errorTypes.find(e => e.Value === errorTypeValue);
    return errorType ? errorType.Name : `Unknown Error (${errorTypeValue})`;
  }

  public retrieveFailedAudits(filterParams: IFailedAuditFilter): Observable<IFailedAuditEvent[]> {
    let queryParams: HttpParams = new HttpParams();

    if (filterParams) {
      if (filterParams.eventTypeValue) {
        queryParams = queryParams.append('eventTypeValue', filterParams.eventTypeValue);
      }
      if (filterParams.pageNumber) {
        queryParams = queryParams.append('pageNumber', filterParams.pageNumber);
      }
      if (filterParams.pageSize) {
        queryParams = queryParams.append('pageSize', filterParams.pageSize);
      }
      if (filterParams.fromDate) {
        queryParams = queryParams.append('fromDate', filterParams.fromDate.toDateString());
      }
      if (filterParams.toDate) {
        queryParams = queryParams.append('toDate', filterParams.toDate.toDateString());
      }
      if (filterParams.genericContentSearch) {
        queryParams = queryParams.append('genericContentSearch', filterParams.genericContentSearch);
      }
      if (filterParams.sourceTypeId) {
        queryParams = queryParams.append('sourceTypeId', filterParams.sourceTypeId);
      }
    }

    return this.apiService.MakeGetRequest<IFailedAuditPagedResponse>('audit/failed', queryParams).pipe(map((failedAuditsP) => {
      this.failedAuditStore.update(setProp('failedAuditResponse', failedAuditsP));
      return failedAuditsP.Data;
    }));
  }

}
